import { AgentJobStatus, ValueTypeEnum, BaseDocumentType } from "~/graphql/supergraphEnums";
import { type DocumentValueInput, type DocumentValueType } from "~/graphql/supergraphTypes";
import { getEffectiveExpiryDate, isDateExpired } from "~/utils/datetime";
import { DOCUMENT_TYPE_MAPPING, INITIAL_VALUES, metadataConfigurations } from "./constants";
import { idDocumentTypesArray } from "./DocumentDetailsFormValues/IdDocFormValues/constants";
import {
  DocumentType,
  DocumentTypes,
  FormValues,
  ProfileTypes,
  ExtractedDataResult,
} from "./types";

/**
 * Determines if a document date field value is unchanged from the backend-provided initial value.
 * Used to decide whether to show expiry warnings — we only warn when the value is what the backend
 * returned, not when the user is actively picking a new date.
 */
export const isDateUnchangedFromBackend = (
  currentValue: Date | undefined,
  initialValue: Date | undefined,
): boolean => {
  if (currentValue instanceof Date && initialValue instanceof Date) {
    return currentValue.getTime() === initialValue.getTime();
  }
  return currentValue === initialValue;
};

/**
 * Document types that do not expire and should be excluded from expiry checks.
 * Uses the actual form values (not readable names).
 */
const NON_EXPIRING_DOCUMENT_TYPES = [
  "W-9",
  "W-8ECI",
  "individual_self_certification",
  "entity_self_certification",
];

/**
 * Determines if a document should be considered expired based on its date values.
 * Uses the backend-computed expiry (documentExpiryDate) when available, falling back to the field date.
 * Only returns true when the date is unchanged from the backend (to avoid false warnings during edits).
 *
 * For document types like AML reliance letter, address verification, and W-series tax forms,
 * the field holds the date of issue/signature while the backend stores the real expiry
 * (e.g. date + 3 years) in the top-level expiryDate field.
 *
 * W-9, W-8ECI, and Self-Certification documents are excluded from expiry checks as they do not expire.
 */
export const isDocumentExpired = (
  currentDate: Date | undefined,
  initialDate: Date | undefined,
  documentExpiryDate: Date | undefined,
  secondaryDocumentType?: string,
): boolean => {
  if (!currentDate) return false;
  if (secondaryDocumentType && NON_EXPIRING_DOCUMENT_TYPES.includes(secondaryDocumentType))
    return false;
  if (!isDateUnchangedFromBackend(currentDate, initialDate)) return false;

  const effectiveExpiryDate = getEffectiveExpiryDate(currentDate, documentExpiryDate);
  return isDateExpired(effectiveExpiryDate);
};

/**
 * Parses a date string (date-only or datetime with timezone) into a local midnight Date
 * using the UTC date components. This ensures the displayed calendar date always matches
 * what the backend extracted from the UTC portion of the stored value — e.g.
 * "2026-03-23T18:30:00Z" (IST midnight) is treated as March 23, not March 24.
 */
const parseDateMetadata = (value: string | undefined | null): Date | undefined => {
  if (!value) return undefined;
  const d = new Date(value);
  if (isNaN(d.getTime())) return undefined;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/**
 * Converts a local Date to a UTC midnight Date so that JSON serialization (toISOString)
 * sends the same calendar date the user selected, regardless of their local timezone offset.
 * e.g. picking March 25 in UTC+1 produces "2026-03-25T00:00:00.000Z", not "2026-03-24T23:00:00Z".
 */
const toUTCMidnight = (date: Date): Date =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

const TAX_DOCUMENT_TYPES = [
  DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue,
  DOCUMENT_TYPE_MAPPING.selfCertification.enumValue,
];

/**
 * Determines if a document type is a tax document type that has detailed metadata.
 * Used to decide whether to show the reset warning modal when changing
 * profile, document type, or sub-type.
 */
export const isTaxDocumentType = (documentType: string | undefined): boolean => {
  if (!documentType) return false;
  return TAX_DOCUMENT_TYPES.includes(documentType);
};

/**
 * Type guard to check if a string is a valid BaseDocumentType enum value.
 */
const BASE_DOCUMENT_TYPE_VALUES = Object.values(BaseDocumentType) as string[];

export const isBaseDocumentType = (value: string | undefined): value is BaseDocumentType => {
  if (!value) return false;
  return BASE_DOCUMENT_TYPE_VALUES.includes(value);
};

export const buildMetadata = (
  values: FormValues,
  existingMetadata?: DocumentValueType[] | null,
): DocumentValueInput[] => {
  const documentType = values.documentType ?? "";

  // Get the metadata structure of the current document type
  // The goal of this method is then fill out the values of this configuration given the values
  const currentDocumentConfiguration = metadataConfigurations[documentType] || [];

  // Keys managed by DocumentDetailsRightSidebar form
  const sidebarManagedKeys = new Set(currentDocumentConfiguration.map(({ key }) => key));

  // Dump the form values into a new map. These should be a 1:1 map to the keys in
  // currentDocuemntConfiguration
  const inputValuesMap: { [key: string]: any } = {
    type: values.secondaryDocumentType,
    certified: values.isCertifiedDocument?.toString(),
    expiry: values.expiryDate,
    description: values.description,
    date: values.date,
    assessment: values.assessment,
    collection_status: values.collectionStatus,
    as_of_date: values.asOfDate,
    name_of_issuer: values.nameOfIssuer,
    issuer_relationship: values.issuerRelationship,
    meets_requirements: values.meetsRequirements,
    entity_name: values.entityName,
  };

  // Go through the expected metadata result and correlate the expected keys from the
  // currentDocumentConfiguration and have them mapped to the corresponding values in inputValuesMap
  const finalValues = currentDocumentConfiguration
    .map(({ key, type }) => {
      let value = inputValuesMap[key] || "";

      // For date-type fields, normalize to UTC midnight of the local date so the backend
      // extracts the same calendar date the user selected, regardless of timezone.
      if (type === ValueTypeEnum.Date && value instanceof Date) {
        value = toUTCMidnight(value);
      }

      if (value !== undefined && value !== null && value !== "") {
        const result: DocumentValueInput = { key, value };

        // Some fields also have type information; include that in the result
        if (type) {
          result.type = type;
        }
        return result;
      }

      return null;
    })
    .filter((item): item is DocumentValueInput => item !== null);

  // For withholding_statement subtype, add entity_name to the metadata
  const isWithholdingStatement =
    documentType === "w_series_tax_form" &&
    values.secondaryDocumentType === "withholding_statement";

  if (
    isWithholdingStatement &&
    values.entityName &&
    !finalValues.some((v) => v.key === "entity_name")
  ) {
    finalValues.push({ key: "entity_name", value: values.entityName });
  }

  // For W-series and Self-Certification documents, preserve existing tax metadata
  // that was saved via TaxFormPage (keys not managed by this sidebar form)
  if (isTaxDocumentType(documentType) && existingMetadata?.length) {
    // For withholding_statement, also exclude entity_name from preservation since we manage it
    const additionalManagedKeys = isWithholdingStatement ? ["entity_name"] : [];
    const allManagedKeys = new Set([...sidebarManagedKeys, ...additionalManagedKeys]);

    const preservedTaxMetadata: DocumentValueInput[] = existingMetadata
      .filter((item): item is DocumentValueType => item !== null && item.key !== null)
      .filter((item) => !allManagedKeys.has(item.key))
      .map((item) => ({
        key: item.key,
        value: item.value ?? "",
        ...(item.type && { type: item.type as ValueTypeEnum }),
      }));

    return [...finalValues, ...preservedTaxMetadata];
  }

  return finalValues;
};

const findValueGivenKeyFromMetadata = (
  key: string,
  currentDocumentMetadata: DocumentValueType[] | null,
): string | Date | undefined => {
  return currentDocumentMetadata?.find((value) => value?.key === key)?.value;
};

export const buildInitialValues = (
  currentDocument: DocumentType | undefined,
  profiles: ProfileTypes | null,
  extractedData: ExtractedDataResult | null,
): FormValues => {
  const currentDocumentMetadata = currentDocument ? currentDocument.metadata : [];
  if (!currentDocument) {
    return INITIAL_VALUES;
  }

  const valueMap: { [key: string]: any } = {
    type: findValueGivenKeyFromMetadata("type", currentDocumentMetadata) ?? "",
    certified: findValueGivenKeyFromMetadata("certified", currentDocumentMetadata) === "true",
    expiry: findValueGivenKeyFromMetadata("expiry", currentDocumentMetadata),
    description: findValueGivenKeyFromMetadata("description", currentDocumentMetadata) ?? "",
    date: findValueGivenKeyFromMetadata("date", currentDocumentMetadata),
    assessment: findValueGivenKeyFromMetadata("assessment", currentDocumentMetadata) ?? "",
    collection_status:
      findValueGivenKeyFromMetadata("collection_status", currentDocumentMetadata) ?? "",
    as_of_date: findValueGivenKeyFromMetadata("as_of_date", currentDocumentMetadata),
    name_of_issuer:
      findValueGivenKeyFromMetadata("name_of_issuer", currentDocumentMetadata) ?? "",
    issuer_relationship:
      findValueGivenKeyFromMetadata("issuer_relationship", currentDocumentMetadata) ?? "",
    meets_requirements:
      findValueGivenKeyFromMetadata("meets_requirements", currentDocumentMetadata) ?? "",
    document_type: currentDocument.documentPurpose?.baseType ?? undefined,
    related_profile: currentDocument.profileId ?? "",
    document_status: currentDocument.status ?? "",
  };

  let expiry: Date | undefined;

  if (valueMap["expiry"]) {
    expiry = parseDateMetadata(valueMap["expiry"] as string);
  } else if (extractedData?.DOCUMENT_TYPE !== "other" && extractedData?.EXPIRATION_DATE) {
    expiry = parseDateMetadata(extractedData.EXPIRATION_DATE);
  } else {
    expiry = undefined;
  }

  let inferred_doc_type = "";
  let inferred_secondary_doc_type = "";

  if (extractedData?.DOCUMENT_TYPE !== "other") {
    for (const docType of idDocumentTypesArray) {
      if (extractedData?.DOCUMENT_TYPE === docType.value) {
        inferred_doc_type = "id";
        inferred_secondary_doc_type = docType.value;
        break;
      }
    }
  }

  const documentType = valueMap["document_type"]
    ? (valueMap["document_type"] as BaseDocumentType)
    : (inferred_doc_type as BaseDocumentType);

  const secondaryDocumentType = valueMap["type"]
    ? (valueMap["type"] as string)
    : inferred_secondary_doc_type;

  const relatedProfile = valueMap["related_profile"]
    ? String(valueMap["related_profile"])
    : extractedData?.DOCUMENT_TYPE !== "other"
      ? String(findFirstMatchingProfileFromExtractedData(profiles, extractedData) ?? "")
      : "";

  const entityName =
    (findValueGivenKeyFromMetadata("entity_name", currentDocumentMetadata) as string) ?? "";

  // Determine if tax details exist (metadata keys beyond sidebar-managed keys)
  const sidebarManagedTaxKeys = new Set([
    "type",
    "date",
    "description",
    "entity_name",
    "document_type",
  ]);
  const hasTaxDetails =
    isTaxDocumentType(documentType) &&
    (currentDocumentMetadata ?? []).some(
      (item) => item?.key && !sidebarManagedTaxKeys.has(item.key) && item.value,
    );

  return {
    relatedProfile: relatedProfile,
    documentType: documentType,
    isCertifiedDocument: valueMap["certified"],
    secondaryDocumentType: secondaryDocumentType,
    documentStatus: valueMap["document_status"],
    expiryDate: expiry,
    description: valueMap["description"],
    date: parseDateMetadata(valueMap["date"] as string),
    documentExpiryDate: parseDateMetadata(currentDocument.expiryDate),
    assessment: valueMap["assessment"],
    collectionStatus: valueMap["collection_status"],
    asOfDate: parseDateMetadata(valueMap["as_of_date"] as string),
    nameOfIssuer: valueMap["name_of_issuer"],
    issuerRelationship: valueMap["issuer_relationship"],
    meetsRequirements: valueMap["meets_requirements"],
    entityName,
    hasTaxDetails,
  };
};

/**
 * Finds the next document in the list that has not yet been completed. This will wrap around if necessary.
 * Example (C = Current Doc (completed), X = Previous Completed, . = Incomplete):
 *
 * [C..] [CX.] [.CX] [.CX.] [X..CX]
 *   ^      ^   ^        ^    ^
 *
 * This does a linear search rather than a binary search since while when this is written values comes back
 * in increasing id order, that is not a guaranteed behavior moving forward.
 *
 * @param currentDocumentId The document id that was just completed
 * @param documentList The full list of documents
 * @returns The next available ID or -1 if none could be found that satisfy the requirements
 */
export const getNextIncompleteDocumentId = (
  currentDocumentId: number | undefined,
  documentList: DocumentTypes | undefined,
): number => {
  const NO_DOC_FOUND = -1;

  if (!currentDocumentId || !documentList) {
    return NO_DOC_FOUND;
  }

  let foundIndex = -1;
  const numDocs = documentList.length;

  // First loop first tries to find the document then once its discovered tries to find the
  // next available document
  for (let i = 0; i < numDocs; i++) {
    if (foundIndex >= 0) {
      if (documentList[i].status == "new") {
        return documentList[i].id;
      }
    } else if (currentDocumentId == documentList[i].id) {
      foundIndex = i;
    }
  }

  // Either we didn't find the document in the list (unlikely) or it was the first item
  // in the list, meaning we've already searched the entire list and there were no open docs
  if (foundIndex <= 0) {
    return NO_DOC_FOUND;
  }

  // Second loop is in case we needed to wrap around
  for (let i = 0; i < foundIndex; i++) {
    if (documentList[i].status == "new") {
      return documentList[i].id;
    }
  }

  return NO_DOC_FOUND;
};

/**
 * Returns the number of remaining incomplete documents. If the current document is filled out but not saved it is
 * still considered incomplete.
 *
 * @param documentList The list of documents.
 * @returns The number of incomplete documents. If no list is passed, 0 is returned.
 */
export const remainingIncompleteDocuments = (
  documentList: DocumentTypes | undefined,
): number => {
  if (!documentList) {
    return 0;
  }

  const initialCount = 0;
  return documentList.reduce(
    (accumulator, doc) => accumulator + (doc.status == "new" ? 1 : 0),
    initialCount,
  );
};

/**
 * Returns the document given the id of the document.
 *
 * @param documentId The id of the document to find.
 * @param documentList The list to find it in.
 * @returns The document or undefined if it is not found.
 */
export const findDocument = (
  documentId: number | undefined,
  documentList: DocumentTypes | undefined,
): DocumentType | undefined => {
  if (!documentId || !documentList) {
    return undefined;
  }

  return documentList.find((doc) => doc.id == documentId);
};

/**
 * Returns whether or not we should show the "save and exit" button. This takes into
 * account what the current document is as well as the number of incomplete documents.
 * If there are no incomplete documents or the user is on the last incomplete document,
 * this returns true, otherwise this returns false.
 *
 * @param currentDocumentId The id of the current document.
 * @param documentList The list of documents to investigate.
 * @returns True if the user should see a "Save and exit" button & false if "Save and next"
 *          should be shown.
 */
export const shouldShowSaveAndExit = (
  currentDocumentId: number | undefined,
  documentList: DocumentTypes | undefined,
): boolean => {
  const numIncompleteDocs = remainingIncompleteDocuments(documentList);
  // No incomplete docs, can show "save and exit"
  if (numIncompleteDocs == 0) {
    return true;
  }

  // Too many incomplete document, must be "save and next"
  if (numIncompleteDocs > 1) {
    return false;
  }

  // Return if this document is the last incomplete document
  return findDocument(currentDocumentId, documentList)?.status == "new";
};

export const findFirstMatchingProfileFromExtractedData = (
  profiles: ProfileTypes | null,
  extractedData: ExtractedDataResult | null,
): number | undefined => {
  if (!profiles || !extractedData?.NAME_DETAILS) return undefined;

  const matchingProfile = profiles.filter((profile) => {
    if (!profile) return false;
    const normalize = (str?: string | null) => (str?.trim() as string)?.toLowerCase();
    const firstName = normalize(profile.firstName);
    const lastName = normalize(profile.lastName);
    const middleName = normalize(profile.middleName);
    const extractedFirstName = normalize(extractedData.NAME_DETAILS?.FIRST_NAME);
    const extractedLastName = normalize(extractedData.NAME_DETAILS?.LAST_NAME);
    const extractedMiddleName = normalize(extractedData.NAME_DETAILS?.MIDDLE_NAME);

    const firstNameMatch = firstName === extractedFirstName;
    const lastNameMatch = lastName === extractedLastName;
    const middleNameMatch = middleName === extractedMiddleName;
    const lastNameAsMiddleMatch = lastName === extractedMiddleName;
    const middleNameAsLastMatch = middleName === extractedLastName;

    return (
      firstNameMatch &&
      (lastNameMatch || middleNameMatch || lastNameAsMiddleMatch || middleNameAsLastMatch)
    );
  })[0];

  return matchingProfile?.id;
};

/**
 * Checks if the document extraction status is terminal (completed, failed, or ignored)
 * @param status - The extraction status to check
 * @returns True if the status is terminal, false otherwise
 */
export const isTerminalExtractionStatus = (status: string | null | undefined): boolean => {
  if (!status) return true;
  return status === AgentJobStatus.Succeeded || status === AgentJobStatus.Failed;
};
